import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, LogIn, ArrowRight } from 'lucide-react';
import { authUtils } from '../utils/auth';

const Login = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Check if already logged in
  useEffect(() => {
    if (authUtils.isAuthenticated()) {
      console.log('✅ Already authenticated, redirecting to dashboard');
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  // WICHTIG: Automatische API-URL Erkennung ohne doppeltes /api
  const getApiUrl = () => {
    if (window.location.hostname === 'localhost') {
      return 'http://localhost:3001/api';
    }
    return 'https://app.alpenscreen.ch/api';
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const API_URL = getApiUrl();
      console.log('API Request to:', `${API_URL}/auth/login`);
      
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('login.errors.loginFailed'));
      }

// Save token and user data mit authUtils
      authUtils.setAuth(data.token, data.user);

      // ✨ Smooth Fade-Out vor Navigation
      document.body.style.opacity = '0';
      document.body.style.transition = 'opacity 0.2s ease';

      setTimeout(() => {
        navigate('/dashboard');
        // Reset opacity after navigation
        setTimeout(() => {
          document.body.style.opacity = '1';
        }, 50);
      }, 200);

    } catch (err) {
      console.error('Login error:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1E7FCC] via-[#1B6DB8] to-[#185CA4] flex items-center justify-center p-4 md:p-8">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-6xl overflow-hidden">
        <div className="grid md:grid-cols-[45%_55%] gap-0">
          
          {/* Left Side - Branding & Info */}
          <div className="bg-gradient-to-br from-[#1E7FCC] to-[#185CA4] p-6 md:p-10 text-white relative overflow-hidden flex flex-col justify-center">
            <div className="absolute inset-0 opacity-10">
              <svg className="w-full h-full" viewBox="0 0 400 400" preserveAspectRatio="none" fill="none">
                <path d="M0 300 L100 200 L200 250 L300 150 L400 200 L400 400 L0 400 Z" fill="white" opacity="0.3"/>
                <path d="M0 350 L150 250 L250 280 L400 220 L400 400 L0 400 Z" fill="white" opacity="0.2"/>
              </svg>
            </div>
            
            <div className="relative z-10">
              <div className="mb-12">
                <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                  {t('login.dashboardTitle')}
                </h1>
                <p className="text-blue-50 text-xl md:text-2xl leading-relaxed font-light">
                  {t('login.dashboardSubtitle')}
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="flex-shrink-0 w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-[#1E7FCC]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-semibold text-lg mb-1">{t('login.features.manageScreens.title')}</h3>
                    <p className="text-blue-50 text-sm leading-relaxed font-light">
                      {t('login.features.manageScreens.description')}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="flex-shrink-0 w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-[#1E7FCC]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-semibold text-lg mb-1">{t('login.features.uploadMedia.title')}</h3>
                    <p className="text-blue-50 text-sm leading-relaxed font-light">
                      {t('login.features.uploadMedia.description')}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="flex-shrink-0 w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-[#1E7FCC]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-semibold text-lg mb-1">{t('login.features.createPlaylists.title')}</h3>
                    <p className="text-blue-50 text-sm leading-relaxed font-light">
                      {t('login.features.createPlaylists.description')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="flex-shrink-0 w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-[#1E7FCC]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-semibold text-lg mb-1">{t('login.features.addWidgets.title')}</h3>
                    <p className="text-blue-50 text-sm leading-relaxed font-light">
                      {t('login.features.addWidgets.description')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="flex-shrink-0 w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-[#1E7FCC]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-semibold text-lg mb-1">{t('login.features.liveUpdates.title')}</h3>
                    <p className="text-blue-50 text-sm leading-relaxed font-light">
                      {t('login.features.liveUpdates.description')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Login Form */}
          <div className="pt-3 pb-6 px-8 md:pt-4 md:pb-8 md:px-16 flex flex-col bg-white relative">
            {/* Logo Centered at Top */}
            <div 
              className="mb-3 text-center cursor-pointer"
              onClick={() => navigate('/')}
            >
              <img 
                src="/assets/logo/alpenscreen-ultra-transparent-512px.png" 
                alt="AlpenScreen Logo" 
                className="w-56 md:w-80 h-auto mx-auto"
              />
            </div>

            <div className="flex-1 flex flex-col justify-center">
              <form onSubmit={handleLogin} className="space-y-6">
                <div className="text-center mb-4">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-50 rounded-2xl mb-5 shadow-md">
                    <LogIn className="w-10 h-10 text-[#1E7FCC]" />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                    {t('login.title')}
                  </h2>
                  <p className="text-gray-600 text-base font-medium">
                    {t('login.signInToContinue')}
                  </p>
                </div>

                {error && (
                  <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 animate-slide-in">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="w-5 h-5 text-red-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <p className="ml-3 text-sm font-medium text-red-800">{error}</p>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">
                    {t('login.emailLabel')}
                  </label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                      <Mail size={20} />
                    </div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full pl-12 pr-4 py-4 border-2 border-gray-300 rounded-xl text-base focus:outline-none focus:ring-4 focus:ring-blue-500/30 focus:border-[#1E7FCC] transition-all"
                      placeholder={t('login.emailPlaceholder')}
                      autoComplete="email"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      {t('login.passwordLabel')}
                    </label>
                    <button
                      type="button"
                      onClick={() => navigate('/request-password-reset')}
                      className="text-sm font-semibold text-[#1E7FCC] hover:text-[#1B6DB8] transition-colors"
                    >
                      {t('login.forgotPassword')}
                    </button>
                  </div>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                      <Lock size={20} />
                    </div>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="w-full pl-12 pr-4 py-4 border-2 border-gray-300 rounded-xl text-base focus:outline-none focus:ring-4 focus:ring-blue-500/30 focus:border-[#1E7FCC] transition-all"
                      placeholder={t('login.passwordPlaceholder')}
                      autoComplete="current-password"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className={`
                    w-full py-4 px-6 rounded-xl font-bold text-lg
                    transition-all duration-200 transform
                    focus:outline-none focus:ring-4 focus:ring-blue-500/50
                    flex items-center justify-center gap-2
                    ${loading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-[#1E7FCC] to-[#185CA4] text-white hover:from-[#1B6DB8] hover:to-[#164E91] hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0'
                    }
                  `}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span>{t('login.signingIn')}</span>
                    </>
                  ) : (
                    <>
                      <LogIn size={20} />
                      <span>{t('login.signInButton')}</span>
                    </>
                  )}
                </button>

                <div className="text-center pt-4 border-t border-gray-100">
                  <p className="text-base text-gray-600">
                    {t('login.noAccount')}{' '}
                    <button
                      type="button"
                      onClick={() => navigate('/register')}
                      className="font-semibold text-[#1E7FCC] hover:text-[#1B6DB8] hover:underline transition-colors inline-flex items-center gap-1"
                    >
                      {t('login.registerNow')}
                      <ArrowRight size={16} />
                    </button>
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slide-in {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Login;